/**
 * Service centralisé de gestion d'erreurs
 *
 * Fournit:
 * - Handler global Vue pour les erreurs non capturées
 * - Handler pour les rejets de promesses non gérés
 * - Logging centralisé des erreurs
 * - Gestion des erreurs HTTP (401, 403, 404, 500, etc.)
 * - Pattern unifié pour la gestion d'erreurs dans les composants
 */

import { ElMessage, ElMessageBox } from 'element-plus';
import { removeToken } from '@/utils/auth';

// Import dynamique du router pour éviter les problèmes de dépendances cycliques
let router = null;
const getRouter = async () => {
  if (!router) {
    const module = await import('@/router');
    router = module.default;
  }
  return router;
};

// Configuration
const CONFIG = {
  // Activer le logging en console (désactiver en production si besoin)
  enableConsoleLog: process.env.NODE_ENV !== 'production',
  // Activer l'envoi des erreurs au backend (à implémenter selon vos besoins)
  enableRemoteLogging: false,
  // URL de l'endpoint de logging (si enableRemoteLogging est true)
  remoteLogUrl: '/api/logs/errors',
  // Durée des notifications en ms
  notificationDuration: 5000,
  // Erreurs à ignorer (ne pas logger)
  ignoredErrors: [
    'ResizeObserver loop',
    'cancelled', // Requêtes annulées volontairement
    'Network Error', // Géré séparément
    'Loading chunk', // Chunk obsolète après déploiement — géré par router.onError (reload)
    'Loading CSS chunk',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
  ]
};

/**
 * Types d'erreurs
 */
export const ErrorTypes = {
  NETWORK: 'NETWORK',
  VALIDATION: 'VALIDATION',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER',
  CLIENT: 'CLIENT',
  UNKNOWN: 'UNKNOWN'
};

/**
 * Classe d'erreur applicative enrichie
 */
export class AppError extends Error {
  constructor(message, type = ErrorTypes.UNKNOWN, details = {}) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.details = details;
    this.timestamp = new Date().toISOString();
    this.handled = false;
  }
}

/**
 * Détermine le type d'erreur à partir d'une erreur HTTP
 * @param {Object} error - Erreur axios ou similaire
 * @returns {string} Type d'erreur
 */
function getErrorType(error) {
  if (!error.response) {
    return error.isNetworkError ? ErrorTypes.NETWORK : ErrorTypes.CLIENT;
  }

  const status = error.response?.status;

  switch (status) {
    case 400:
      return ErrorTypes.VALIDATION;
    case 401:
      return ErrorTypes.AUTHENTICATION;
    case 403:
      return ErrorTypes.AUTHORIZATION;
    case 404:
      return ErrorTypes.NOT_FOUND;
    case 422:
      return ErrorTypes.VALIDATION;
    case 500:
    case 502:
    case 503:
    case 504:
      return ErrorTypes.SERVER;
    default:
      return ErrorTypes.UNKNOWN;
  }
}

/**
 * Formate une erreur pour le logging
 * @param {Error} error - Erreur à formater
 * @param {Object} context - Contexte additionnel
 * @returns {Object} Erreur formatée
 */
function formatErrorForLogging(error, context = {}) {
  return {
    timestamp: new Date().toISOString(),
    message: error.message || 'Erreur inconnue',
    type: error.type || getErrorType(error),
    stack: error.stack,
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    context: {
      ...context,
      route: router?.currentRoute?.value?.path || 'unknown',
      component: context.component || 'unknown'
    },
    response: error.response ? {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data
    } : null
  };
}

/**
 * Log une erreur en console (dev uniquement)
 * @param {Object} formattedError - Erreur formatée
 */
function logToConsole(formattedError) {
  if (!CONFIG.enableConsoleLog) return;

  console.group(`🚨 Erreur [${formattedError.type}]`);
  console.error('Message:', formattedError.message);
  console.info('Timestamp:', formattedError.timestamp);
  console.info('URL:', formattedError.url);
  console.info('Route:', formattedError.context?.route);
  if (formattedError.response) {
    console.info('Response:', formattedError.response);
  }
  if (formattedError.stack) {
    console.debug('Stack:', formattedError.stack);
  }
  console.groupEnd();
}

/**
 * Envoie une erreur au serveur de logging
 * @param {Object} formattedError - Erreur formatée
 */
async function logToRemote(formattedError) {
  if (!CONFIG.enableRemoteLogging) return;

  try {
    // Utilise fetch natif pour éviter les boucles avec axios
    await fetch(CONFIG.remoteLogUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formattedError)
    });
  } catch (e) {
    // Silent fail pour éviter les boucles infinies
    console.warn('Impossible d\'envoyer l\'erreur au serveur:', e.message);
  }
}

/**
 * Vérifie si une erreur doit être ignorée
 * @param {Error} error - Erreur à vérifier
 * @returns {boolean}
 */
function shouldIgnoreError(error) {
  const message = error.message || '';
  return CONFIG.ignoredErrors.some(ignored => message.includes(ignored));
}

/**
 * Messages utilisateur par type d'erreur
 */
const USER_MESSAGES = {
  [ErrorTypes.NETWORK]: 'Problème de connexion. Vérifiez votre connexion internet.',
  [ErrorTypes.VALIDATION]: 'Veuillez vérifier les informations saisies.',
  [ErrorTypes.AUTHENTICATION]: 'Votre session a expiré. Veuillez vous reconnecter.',
  [ErrorTypes.AUTHORIZATION]: 'Vous n\'avez pas les droits pour effectuer cette action.',
  [ErrorTypes.NOT_FOUND]: 'La ressource demandée n\'existe pas.',
  [ErrorTypes.SERVER]: 'Une erreur serveur est survenue. Réessayez plus tard.',
  [ErrorTypes.CLIENT]: 'Une erreur est survenue.',
  [ErrorTypes.UNKNOWN]: 'Une erreur inattendue est survenue.'
};

/**
 * Affiche une notification d'erreur à l'utilisateur
 * @param {string} type - Type d'erreur
 * @param {string} customMessage - Message personnalisé (optionnel)
 */
function showErrorNotification(type, customMessage = null) {
  const message = customMessage || USER_MESSAGES[type] || USER_MESSAGES[ErrorTypes.UNKNOWN];

  ElMessage.error({
    message,
    duration: CONFIG.notificationDuration
  });
}

/**
 * Gère les erreurs 401 (session expirée)
 */
async function handleAuthenticationError() {
  removeToken();
  const routerInstance = await getRouter();

  ElMessageBox.alert(
    'Votre session a expiré. Vous allez être redirigé vers la page de connexion.',
    'Session expirée',
    {
      confirmButtonText: 'OK',
      type: 'warning',
      callback: () => {
        routerInstance.push({ path: '/login', query: { redirect: routerInstance.currentRoute.value.fullPath } });
      }
    }
  );
}

/**
 * Gère les erreurs 403 (accès refusé)
 * @param {Object} error - Erreur originale
 */
async function handleAuthorizationError(error) {
  const message = error.response?.data?.message || 'Vous n\'avez pas les droits pour effectuer cette action.';
  const routerInstance = await getRouter();

  ElMessageBox.alert(
    message,
    'Accès refusé',
    {
      confirmButtonText: 'Retour',
      type: 'error',
      callback: () => {
        // Retour à la page précédente ou au dashboard
        if (typeof window !== 'undefined' && window.history.length > 1) {
          routerInstance.back();
        } else {
          routerInstance.push('/');
        }
      }
    }
  );
}

/**
 * Fonction principale de gestion d'erreur
 * Peut être utilisée dans les composants pour une gestion uniforme
 *
 * @param {Error} error - Erreur à gérer
 * @param {Object} options - Options de gestion
 * @param {string} options.context - Contexte (nom du composant/action)
 * @param {boolean} options.showNotification - Afficher une notification (défaut: true)
 * @param {string} options.customMessage - Message personnalisé
 * @param {boolean} options.rethrow - Relancer l'erreur après traitement
 * @returns {Object|null} Erreurs de validation si présentes
 */
export function handleError(error, options = {}) {
  const {
    context = '',
    showNotification = true,
    customMessage = null,
    rethrow = false
  } = options;

  // Ignorer certaines erreurs
  if (shouldIgnoreError(error)) {
    return null;
  }

  const errorType = error.type || getErrorType(error);
  const formattedError = formatErrorForLogging(error, { component: context });

  // Logging
  logToConsole(formattedError);
  logToRemote(formattedError);

  // Gestion spécifique par type
  switch (errorType) {
    case ErrorTypes.AUTHENTICATION:
      handleAuthenticationError();
      break;

    case ErrorTypes.AUTHORIZATION:
      handleAuthorizationError(error);
      break;

    case ErrorTypes.VALIDATION:
      // Retourner les erreurs de validation pour affichage dans le formulaire
      if (error.response?.data?.errors) {
        if (showNotification) {
          showErrorNotification(errorType, customMessage);
        }
        return error.response.data.errors;
      }
      if (showNotification) {
        showErrorNotification(errorType, customMessage);
      }
      break;

    default:
      if (showNotification) {
        showErrorNotification(errorType, customMessage);
      }
  }

  // Relancer si demandé
  if (rethrow) {
    throw error;
  }

  return null;
}

/**
 * Wrapper pour les appels API avec gestion d'erreur intégrée
 *
 * @param {Promise} apiCall - Promesse de l'appel API
 * @param {Object} options - Options de gestion d'erreur
 * @returns {Promise<[data, error]>} Tuple [données, erreur]
 *
 * @example
 * const [data, error] = await safeApiCall(
 *   userResource.get(userId),
 *   { context: 'UserProfile.fetchUser' }
 * );
 * if (error) {
 *   this.validationErrors = error; // Si erreur de validation
 *   return;
 * }
 * this.user = data;
 */
export async function safeApiCall(apiCall, options = {}) {
  try {
    const data = await apiCall;
    return [data, null];
  } catch (error) {
    const validationErrors = handleError(error, options);
    return [null, validationErrors || error];
  }
}

/**
 * Crée un handler d'erreur pour un composant spécifique
 * Utile pour réutiliser le même contexte dans plusieurs méthodes
 *
 * @param {string} componentName - Nom du composant
 * @returns {Function} Handler d'erreur configuré
 *
 * @example
 * // Dans le composant
 * const handleError = createErrorHandler('UserDialog');
 *
 * // Utilisation
 * .catch(handleError)
 * // ou avec options
 * .catch(error => handleError(error, { customMessage: 'Erreur lors de la sauvegarde' }))
 */
export function createErrorHandler(componentName) {
  return (error, additionalOptions = {}) => {
    return handleError(error, {
      context: componentName,
      ...additionalOptions
    });
  };
}

/**
 * Initialise les handlers globaux d'erreur
 * À appeler dans main.js
 *
 * @param {Object} app - Instance de l'application Vue
 */
export function initGlobalErrorHandlers(app) {
  // Handler Vue pour les erreurs dans les composants
  app.config.errorHandler = (error, instance, info) => {
    const componentName = instance?.$options?.name || instance?.$.type?.name || 'UnknownComponent';

    handleError(error, {
      context: `${componentName} (${info})`,
      showNotification: true
    });
  };

  // Handler Vue pour les warnings (dev uniquement)
  if (process.env.NODE_ENV !== 'production') {
    app.config.warnHandler = (msg, instance, trace) => {
      console.warn(`[Vue warn]: ${msg}`, trace);
    };
  }

  // Handler global pour les promesses non gérées
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;

    // Éviter de traiter les annulations de MessageBox
    if (error === 'cancel' || error === 'close') {
      event.preventDefault();
      return;
    }

    handleError(error, {
      context: 'UnhandledPromiseRejection',
      showNotification: true
    });

    // Empêcher l'affichage de l'erreur dans la console du navigateur
    event.preventDefault();
  });

  // Handler global pour les erreurs JavaScript
  window.addEventListener('error', (event) => {
    // Ignorer les erreurs ResizeObserver
    if (event.message?.includes('ResizeObserver')) {
      event.preventDefault();
      return;
    }

    handleError(event.error || new Error(event.message), {
      context: `GlobalError (${event.filename}:${event.lineno})`,
      showNotification: false // Éviter le spam de notifications
    });
  });

  console.info('✅ Handlers globaux d\'erreurs initialisés');
}

/**
 * Composable pour la gestion d'erreurs dans les composants Vue 3 (Composition API)
 *
 * @param {string} componentName - Nom du composant
 * @returns {Object} Méthodes et état de gestion d'erreurs
 *
 * @example
 * setup() {
 *   const { errors, handleError, clearErrors, safeCall } = useErrorHandler('MyComponent');
 *
 *   const fetchData = async () => {
 *     const [data, error] = await safeCall(api.getData());
 *     if (!error) {
 *       // success
 *     }
 *   };
 *
 *   return { errors, fetchData };
 * }
 */
export function useErrorHandler(componentName) {
  const errors = { value: null };

  const handle = (error, options = {}) => {
    const result = handleError(error, {
      context: componentName,
      ...options
    });
    if (result) {
      errors.value = result;
    }
    return result;
  };

  const clearErrors = () => {
    errors.value = null;
  };

  const safeCall = async (apiCall, options = {}) => {
    clearErrors();
    const [data, error] = await safeApiCall(apiCall, {
      context: componentName,
      ...options
    });
    if (error && typeof error === 'object' && !error.message) {
      errors.value = error;
    }
    return [data, error];
  };

  return {
    errors,
    handleError: handle,
    clearErrors,
    safeCall
  };
}

export default {
  handleError,
  safeApiCall,
  createErrorHandler,
  initGlobalErrorHandlers,
  useErrorHandler,
  ErrorTypes,
  AppError
};
